import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react';
import { clsx } from 'clsx';
import styles from './interior.module.css';

interface SourceChartProps {
  caption: string;
  scaleLabel: string;
  /** Shared numeric maximum, in the same units as each value. Never inferred per row. */
  max: number;
  /** Optional caller-owned motion; static SSR is the default. */
  barStyle?: (index: number, percentage: number) => CSSProperties;
  rows: readonly {
    id: string;
    label: string;
    value: number;
    display: string;
    detail?: ReactNode;
    emphasis?: boolean;
  }[];
}

/** Values and denominators are supplied by the research owner, not calculated here. */
export function SourceChart({
  caption,
  scaleLabel,
  max,
  rows,
  barStyle,
}: SourceChartProps): React.ReactElement {
  if (
    !Number.isFinite(max) ||
    max <= 0 ||
    rows.some(({ value }) => !Number.isFinite(value) || value < 0 || value > max)
  ) {
    throw new Error(
      'SourceChart requires finite values between zero and its positive shared maximum'
    );
  }
  return (
    <figure className={styles.sourceChart} aria-label={caption}>
      <figcaption>{caption}</figcaption>
      <dl>
        {rows.map(({ id, ...row }, index) => (
          <SourceChartRow
            key={id}
            {...row}
            max={max}
            barStyle={barStyle?.(index, (row.value / max) * 100)}
          />
        ))}
      </dl>
      <p className={styles.chartScale}>{scaleLabel}</p>
    </figure>
  );
}

interface SourceChartRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  value: number | null;
  display: string;
  max: number;
  detail?: ReactNode;
  emphasis?: boolean;
  baseline?: number;
  rowRef?: Ref<HTMLDivElement>;
  barStyle?: CSSProperties;
}

/** A row for caller-owned filtering, FLIP motion and tooltip interaction inside a dl. */
export function SourceChartRow({
  label,
  value,
  display,
  max,
  detail,
  emphasis,
  baseline,
  rowRef,
  barStyle,
  className,
  ...rest
}: SourceChartRowProps): React.ReactElement {
  if (
    !Number.isFinite(max) ||
    max <= 0 ||
    (value !== null && (!Number.isFinite(value) || value < 0 || value > max)) ||
    (baseline !== undefined && (!Number.isFinite(baseline) || baseline < 0 || baseline > max))
  ) {
    throw new Error(
      'SourceChart requires finite values between zero and its positive shared maximum'
    );
  }
  return (
    <div
      {...rest}
      ref={rowRef}
      className={clsx(styles.chartRow, emphasis && styles.chartEmphasis, className)}
    >
      <dt>
        {label}
        {detail != null && <span className={styles.chartDetail}>{detail}</span>}
      </dt>
      <dd
        className={clsx(styles.chartTrack, value === null && styles.chartUnknown)}
        aria-hidden="true"
      >
        {value !== null && (
          <span aria-hidden="true" style={{ width: `${(value / max) * 100}%`, ...barStyle }} />
        )}
        {baseline !== undefined && (
          <i
            className={styles.chartBaseline}
            style={{
              left: `${(baseline / max) * 100}%`,
              transform: baseline === max ? 'translateX(-100%)' : undefined,
            }}
          />
        )}
      </dd>
      <dd className={styles.chartValue}>{display}</dd>
    </div>
  );
}

export function StatValue({ children }: { children: ReactNode }): React.ReactElement {
  return <span className={styles.statValue}>{children}</span>;
}
